<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRConfirmPopup from "@/components/UI/feedbacks/DRConfirmPopup.vue";
import type { InputInstance } from "element-plus";
import { computed, ref, watch } from "vue";
import type { ScaleConfig } from "@/types/legend.types";
import { editScaleName } from "./utils";
import { AVAILABLE_FONTS, DEFAULT_FONT } from "@/config/fonts";

const props = defineProps<{
  modelValue: ScaleConfig;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: ScaleConfig): void;
  (e: "remove"): void;
}>();

const scale = computed({
  get: () => props.modelValue,
  set: (value: ScaleConfig) => emits("update:modelValue", value),
});

const nameInputRef = ref<InputInstance | null>(null);
const editingName = ref(false);
const name = ref("");

const onEditName = () => {
  editingName.value = true;
  name.value = scale.value.name;
};

const onValidateName = () => {
  const nameValue = name.value.trim();
  if (!nameValue || nameValue === scale.value.name) {
    editingName.value = false;
    name.value = scale.value.name;
    return;
  }
  editingName.value = false;
  scale.value = editScaleName(scale.value, nameValue);
};

watch(nameInputRef, (value) => {
  if (value && editingName.value) {
    value.focus();
  }
});

const size = computed({
  get: () => scale.value.props.textstyle?.size,
  set: (value: number) => {
    const updatedTextstyle = scale.value.props.textstyle
      ? { ...scale.value.props.textstyle, size: value }
      : { name: scale.value.name, size: value };

    scale.value = {
      ...scale.value,
      props: {
        ...scale.value.props,
        textstyle: updatedTextstyle,
      },
    };
  },
});

const selectedFont = computed({
  get: () => {
    const currentFilename =
      scale.value.props.textstyle?.filename || DEFAULT_FONT.filename;
    return (
      AVAILABLE_FONTS.find((f) => f.filename === currentFilename)?.name ||
      DEFAULT_FONT.name
    );
  },
  set: (fontName: string) => {
    const font = AVAILABLE_FONTS.find((f) => f.name === fontName);
    if (font) {
      const updatedTextstyle = scale.value.props.textstyle
        ? {
            ...scale.value.props.textstyle,
            filename: font.filename,
            typeface: font.name,
          }
        : {
            name: scale.value.name,
            filename: font.filename,
            typeface: font.name,
          };

      scale.value = {
        ...scale.value,
        props: {
          ...scale.value.props,
          textstyle: updatedTextstyle,
        },
      };
    }
  },
});

const fontOptions = AVAILABLE_FONTS.map((font) => font.name);
</script>

<template>
  <div
    class="d-flex gap-3 py-2 px-3 rounded mt-2 align-items-center hoverable-white"
  >
    <el-input-number
      class="col-auto"
      v-model="size"
      :min="0"
      :max="100000"
      :step="0.01"
      size="small"
      controls-position="right"
    />

    <el-select
      class="col-auto"
      v-model="selectedFont"
      placeholder="Select font"
      size="small"
      style="width: 150px"
    >
      <el-option
        v-for="font in fontOptions"
        :key="font"
        :label="font"
        :value="font"
      />
    </el-select>

    <el-input
      v-if="editingName"
      class="col"
      v-model="name"
      ref="nameInputRef"
      @keyup.enter="onValidateName"
    ></el-input>

    <div v-else class="col">{{ scale.name }}</div>
    <div class="d-flex gap-1">
      <DRBtnIcon
        v-if="editingName"
        icon="bi-check-lg"
        variant="flat-success"
        :size="30"
        @click="onValidateName"
      />
      <DRBtnIcon
        v-else
        icon="bi-pencil"
        variant="flat"
        :size="30"
        @click="onEditName"
      />

      <DRConfirmPopup :title="$t('confirmDelete')" @confirm="$emit('remove')">
        <DRBtnIcon icon="bi-trash3" variant="flat-danger" :size="30" />
      </DRConfirmPopup>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
